import {
  useGetUserMemberships,
  useOnboardingStepsCompleted,
  usePendingOrganizationInvitations,
  useUserPreferences
} from "@/api";
import Loading from "@/components/ui/loading";
import { SelectOrganization } from "@/features/manage-organizations";
import { Role } from "@/gql/__generated__/graphql";
import { SIMULATE_ENERGY_MACHINES_CUSTOM_URL_HOST } from "@/lib/white-label-branding/BrandLabelConstants";
import { getWhiteLabelBrand } from "@/lib/white-label-branding/getWhiteLabelBrand";
import {
  createFileRoute,
  Navigate,
  NavigateOptions
} from "@tanstack/react-router";
import { useLocalStorage } from "usehooks-ts";

type RouteSearch = {
  reset?: boolean;
};

export const Route = createFileRoute("/_auth/organization")({
  component: SelectOrganizationPage,
  validateSearch: (
    search: Record<string, unknown>
  ): RouteSearch | undefined => ({
    reset: (search?.reset as boolean) || undefined
  })
});

function SelectOrganizationPage() {
  const resetLatestOrganization = Route.useSearch()?.reset;

  const [latestOrganizationSlug] = useLocalStorage<string | null>(
    "latestOrganizationSlug",
    null
  );
  const { data: memberships, loading: loadingMemberships } =
    useGetUserMemberships();
  const { data: invitations, loading: loadingInvitations } =
    usePendingOrganizationInvitations();
  const { isOnboardingCompleted } = useOnboardingStepsCompleted();
  const { data: preferences } = useUserPreferences();
  const brand = getWhiteLabelBrand();

  if (loadingMemberships || loadingInvitations)
    return (
      <div className="h-screen w-full flex justify-center items-center dark:bg-brand-gray-950">
        <Loading size="md" />
      </div>
    );

  if (memberships.length < 1) {
    if (invitations.length === 1)
      return (
        <Navigate
          to="/invitation/$invitationId"
          params={{ invitationId: invitations[0].id }}
          search={{ redirect: undefined }}
        />
      );
    if (invitations.length > 1) return <Navigate to="/invitations" />;

    return (
      <Navigate to="/organization-create" search={{ redirect: undefined }} />
    );
  }

  if (
    (latestOrganizationSlug && !resetLatestOrganization) ||
    memberships.length === 1
  ) {
    const activeOrganizationRole =
      memberships.find(
        (membership) => membership?.organization.slug === latestOrganizationSlug
      )?.role || memberships[0]?.role;

    let to: NavigateOptions["to"] = "/organization/$organizationSlug/gallery";

    if (
      activeOrganizationRole === Role.Admin &&
      !isOnboardingCompleted &&
      !preferences?.onboardingDisabled &&
      brand?.hostname !== SIMULATE_ENERGY_MACHINES_CUSTOM_URL_HOST
    )
      to = "/organization/$organizationSlug/onboarding";

    return (
      <Navigate
        to={to}
        params={{
          organizationSlug:
            latestOrganizationSlug || memberships[0]?.organization.slug
        }}
      />
    );
  }

  return <SelectOrganization />;
}
